import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Input } from '../form/TextField';

type Props = {
  timeZone: string;
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
};

type FormData = {
  date: string;
  time: string;
};

function emptyIfEpoch(date: string) {
  return date === '1970-01-01' ? '' : date;
}

function emptyIfMidnight(time: string) {
  return time === '00:00' ? '' : time;
}

function parseValue(timeZone: string, value: string | null) {
  if (!value) {
    return { date: '', time: '' };
  }

  //const date = roundToNearestMinutes(new Date(value), { nearestTo: 15 });
  const date = new Date(value);

  return {
    date: emptyIfEpoch(formatInTimeZone(date, timeZone, 'yyyy-MM-dd')),
    time: emptyIfMidnight(formatInTimeZone(date, timeZone, 'HH:mm')),
  };
}

function toDate({ date, time }: Partial<FormData>, timeZone: string) {
  if (date === undefined || time === undefined) {
    return null;
  }

  if (
    (date === '' || date === '1970-01-01') &&
    (time === '' || time === '00:00')
  ) {
    return null;
  }

  // Create a date-time string in the specified timezone
  // Format: YYYY-MM-DDTHH:mm (local time in the timezone)
  const dateTimeString = `${date || '1970-01-01'}T${time || '00:00'}`;

  // Convert the local time in the specified timezone to UTC
  // zonedTimeToUtc interprets the date-time string as being in the specified timezone
  const utcDate = zonedTimeToUtc(dateTimeString, timeZone);

  return utcDate.toISOString();
}

export function DateTime({ timeZone, label, value, onChange }: Props) {
  const { control, register, watch, getValues, reset } = useForm<FormData>({
    defaultValues: parseValue(timeZone, value),
  });

  // Update form when value or timeZone props change
  // This ensures the form reflects the latest prop values, not just the initial mount values
  useEffect(() => {
    const parsed = parseValue(timeZone, value);
    reset(parsed);
  }, [value, timeZone, reset]);

  // Once on mount, update the parent value if the local value is different (due to rounding to nearest 5 minute interval)
  useEffect(() => {
    const localValue = toDate(getValues(), timeZone);
    if (localValue && localValue !== value) {
      onChange(localValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      onChange(toDate(value, timeZone));
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, timeZone]);

  return (
    <div className='flex w-full items-stretch gap-3 text-left text-base'>
      <div className='min-w-0 flex-1'>
        <Input {...register('date')} label={label} type='date' />
      </div>
      <Controller
        control={control}
        name='time'
        render={({ field: { value, onChange } }) => (
          <div className='min-w-0 flex-1 pt-[24px]'>
            <input
              type='time'
              className='input form-input mt-0.5 block w-full min-w-0 appearance-none whitespace-nowrap rounded border border-grey-20 bg-white py-2.5 pl-3 pr-2 focus:!border-grey-40'
              style={{ outline: 'none', boxShadow: 'none' }}
              value={value}
              onChange={onChange}
            />
          </div>
        )}
      />
    </div>
  );
}
